import { Link, router } from "@inertiajs/react"
import { CalendarIcon } from "lucide-react"
import { type ReactNode } from "react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { AdminLayout } from "@/layouts/AdminLayout"
import type { ContentStatus, PaginationMeta } from "@/lib/types"

interface EventItem {
  id: number
  name: string
  status: ContentStatus
  starts_at: string | null
  ends_at: string | null
  edit_url: string
}

interface Props {
  region: { id: number; name: string }
  events: EventItem[]
  meta: PaginationMeta
  urls: {
    index: string
    region: string
    new_event: string
  }
}

function statusVariant(status: ContentStatus): "default" | "secondary" | "outline" | "destructive" {
  switch (status) {
    case "approved":      return "default"
    case "rejected":      return "destructive"
    case "pending":       return "secondary"
    case "needs_changes": return "secondary"
    case "draft":         return "outline"
    case "archived":      return "outline"
    default:              return "outline"
  }
}

function PaginationControls({ meta, indexUrl }: { meta: PaginationMeta; indexUrl: string }) {
  if (meta.pages <= 1) return null

  return (
    <div className="flex items-center justify-end gap-2 py-3 text-sm text-muted-foreground">
      {meta.page > 1 && (
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            router.get(indexUrl, { page: meta.page - 1 }, { preserveState: true })
          }
        >
          Previous
        </Button>
      )}
      <span className="flex items-center px-2">
        Page {meta.page} of {meta.pages}
      </span>
      {meta.page < meta.pages && (
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            router.get(indexUrl, { page: meta.page + 1 }, { preserveState: true })
          }
        >
          Next
        </Button>
      )}
    </div>
  )
}

export default function RegionEventsIndex({ region, events, meta, urls }: Props) {
  return (
    <div>
      <div className="mb-6 flex items-start justify-between">
        <div>
          <div className="mb-1 flex items-center gap-2 text-sm text-muted-foreground">
            <Link href={urls.region} className="hover:underline">
              {region.name}
            </Link>
            <span aria-hidden="true">/</span>
            <span>Events</span>
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Events — {region.name}
          </h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {meta.count} event{meta.count !== 1 ? "s" : ""} · Public events curated for this region.
          </p>
        </div>
        <Button asChild size="sm">
          <Link href={urls.new_event}>Add event</Link>
        </Button>
      </div>

      {events.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border py-20 text-center">
          <CalendarIcon className="mb-4 h-10 w-10 text-muted-foreground opacity-30" aria-hidden="true" />
          <p className="text-sm font-medium text-muted-foreground">
            No events yet for {region.name}.
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Add upcoming local events for guests to discover.
          </p>
          <Button asChild size="sm" className="mt-4">
            <Link href={urls.new_event}>Add your first event</Link>
          </Button>
        </div>
      ) : (
        <>
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Starts</TableHead>
                  <TableHead>Ends</TableHead>
                  <TableHead className="w-16"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {events.map((event) => (
                  <TableRow key={event.id}>
                    <TableCell className="font-medium">{event.name}</TableCell>
                    <TableCell>
                      <Badge variant={statusVariant(event.status)} className="capitalize">
                        {event.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {event.starts_at
                        ? new Date(event.starts_at).toLocaleDateString()
                        : "—"}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {event.ends_at
                        ? new Date(event.ends_at).toLocaleDateString()
                        : "—"}
                    </TableCell>
                    <TableCell>
                      <Link
                        href={event.edit_url}
                        className="text-sm text-primary hover:underline"
                      >
                        Edit
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <PaginationControls meta={meta} indexUrl={urls.index} />
        </>
      )}
    </div>
  )
}

RegionEventsIndex.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
